/**
 * Parser for flashcard import text.
 * Parses text in "Question::Answer" format, one per line.
 * - Ignores empty lines and lines without "::" separator
 * - Trims whitespace from questions and answers
 * - Enforces a global limit of 100 flashcards per user
 * - Enforces a per-batch limit of 40 flashcards
 */

const GLOBAL_FLASHCARD_LIMIT = 100
const BATCH_FLASHCARD_LIMIT = 40

/**
 * Parse flashcard text into an array of flashcard objects.
 * Respects both global user limit (100 total) and batch limit (40 per import).
 *
 * @param {string} text - Raw text input with one flashcard per line
 * @param {number} currentCount - Current number of flashcards user already has (default: 0)
 * @returns {Object} Parse result
 * @returns {Array<{question: string, answer: string}>} result.valid - Valid flashcards (max 40 or remaining slots)
 * @returns {Object} result.stats - Parsing statistics
 * @returns {number} result.stats.totalLines - Total number of non-empty lines processed
 * @returns {number} result.stats.validCount - Number of valid flashcards found
 * @returns {number} result.stats.invalidCount - Number of invalid lines skipped
 * @returns {number} result.stats.batchLimit - Maximum per-batch limit (40)
 * @returns {number} result.stats.globalLimit - Maximum total flashcards (100)
 * @returns {number} result.stats.currentCount - Current flashcard count
 * @returns {number} result.stats.availableSlots - Remaining slots before hitting global limit
 * @returns {boolean} result.stats.limitReached - Whether the batch limit was hit
 * @returns {boolean} result.stats.globalLimitReached - Whether global limit prevents any import
 */
export function parseFlashcards(text, currentCount = 0) {
  // Guard: handle null, undefined, or non-string input
  if (!text || typeof text !== 'string') {
    const availableSlots = Math.max(0, GLOBAL_FLASHCARD_LIMIT - currentCount)
    return {
      valid: [],
      stats: {
        totalLines: 0,
        validCount: 0,
        invalidCount: 0,
        batchLimit: BATCH_FLASHCARD_LIMIT,
        globalLimit: GLOBAL_FLASHCARD_LIMIT,
        currentCount,
        availableSlots,
        limitReached: false,
        globalLimitReached: currentCount >= GLOBAL_FLASHCARD_LIMIT,
      },
    }
  }

  const lines = text.split('\n')
  const valid = []
  let invalidCount = 0
  let totalLines = 0

  // Calculate how many slots are available for import
  const availableSlots = Math.max(0, GLOBAL_FLASHCARD_LIMIT - currentCount)
  // Effective limit is the minimum of batch limit and available slots
  const effectiveLimit = Math.min(BATCH_FLASHCARD_LIMIT, availableSlots)

  for (const line of lines) {
    const trimmed = line.trim()

    // Skip empty lines
    if (!trimmed) {
      continue
    }

    totalLines++

    // Check if line contains separator
    if (!trimmed.includes('::')) {
      invalidCount++
      continue
    }

    // Split on first occurrence of "::"
    const separatorIndex = trimmed.indexOf('::')
    const question = trimmed.slice(0, separatorIndex).trim()
    const answer = trimmed.slice(separatorIndex + 2).trim()

    // Skip if question or answer is empty after trimming
    if (!question || !answer) {
      invalidCount++
      continue
    }

    // Add valid flashcard, but stop at effective limit
    if (valid.length < effectiveLimit) {
      valid.push({ question, answer })
    }
  }

  return {
    valid,
    stats: {
      totalLines,
      validCount: valid.length,
      invalidCount,
      batchLimit: BATCH_FLASHCARD_LIMIT,
      globalLimit: GLOBAL_FLASHCARD_LIMIT,
      currentCount,
      availableSlots,
      limitReached: totalLines - invalidCount > effectiveLimit,
      globalLimitReached: currentCount >= GLOBAL_FLASHCARD_LIMIT,
    },
  }
}

/**
 * Calculate parsing statistics without returning the parsed flashcards.
 * Useful for real-time feedback as user types.
 *
 * @param {string} text - Raw text input
 * @param {number} currentCount - Current number of flashcards user already has (default: 0)
 * @returns {Object} Parsing statistics
 * @returns {number} totalLines - Total number of non-empty lines
 * @returns {number} validCount - Number of valid flashcards (respects both limits)
 * @returns {number} invalidCount - Number of invalid lines
 * @returns {number} batchLimit - Maximum per-batch flashcards (40)
 * @returns {number} globalLimit - Maximum total flashcards (100)
 * @returns {number} currentCount - Current flashcard count
 * @returns {number} availableSlots - Remaining slots before global limit
 * @returns {boolean} limitReached - Whether batch limit was hit
 * @returns {boolean} globalLimitReached - Whether global limit prevents import
 */
export function getParserStats(text, currentCount = 0) {
  const result = parseFlashcards(text, currentCount)
  return result.stats
}
